import { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";

const ToolBtn = ({ onCmd, icon: Icon, label, testid }) => (
  <button
    type="button"
    aria-label={label}
    data-testid={testid}
    // preventDefault keeps the editor selection intact when clicking the button
    onMouseDown={(e) => { e.preventDefault(); onCmd(); }}
    className="grid place-items-center h-7 w-7 rounded-[7px] text-[var(--text-muted)] hover:bg-[color:rgba(31,115,85,0.1)] hover:text-[var(--text)] transition-colors"
  >
    <Icon className="h-3.5 w-3.5" />
  </button>
);

export const RichTextEditor = ({ value, onChange, placeholder, minHeight = 120, onExpand, testid }) => {
  const ref = useRef(null);

  // Sync external value into the editor without stealing the caret while typing.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if ((value || "") !== el.innerHTML) el.innerHTML = value || "";
  }, [value]);

  const exec = (command) => {
    const el = ref.current;
    if (el) el.focus();
    document.execCommand(command, false, null);
    if (el) onChange(el.innerHTML);
  };

  const isEmpty = !value || value === "<br>" || value.replace(/<[^>]*>/g, "").trim() === "";

  return (
    <div className="rounded-[10px] border border-[color:var(--border-subtle)] bg-white overflow-hidden" data-testid={testid}>
      <div className="flex items-center gap-0.5 border-b border-[color:var(--border-subtle)] px-1.5 py-1 bg-[var(--surface-1)]">
        <ToolBtn onCmd={() => exec("bold")} icon={Bold} label="Bold" testid={testid ? `${testid}-bold` : undefined} />
        <ToolBtn onCmd={() => exec("italic")} icon={Italic} label="Italic" testid={testid ? `${testid}-italic` : undefined} />
        <span className="mx-0.5 h-4 w-px bg-[color:var(--border-subtle)]" />
        <ToolBtn onCmd={() => exec("insertUnorderedList")} icon={List} label="Bullet list" testid={testid ? `${testid}-bullet` : undefined} />
        <ToolBtn onCmd={() => exec("insertOrderedList")} icon={ListOrdered} label="Numbered list" testid={testid ? `${testid}-number` : undefined} />
        {onExpand && (
          <>
            <span className="flex-1" />
            <ToolBtn onCmd={onExpand} icon={Maximize2} label="Expand editor" testid={testid ? `${testid}-expand` : undefined} />
          </>
        )}
      </div>
      <div className="relative">
        {isEmpty && placeholder && (
          <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-[var(--text-faint)]">{placeholder}</span>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          data-testid={testid ? `${testid}-area` : undefined}
          className={cn("rich-editor px-3 py-2.5 text-sm text-[var(--text)] leading-relaxed focus:outline-none")}
          style={{ minHeight }}
        />
      </div>
    </div>
  );
};
